import { Alert, Button, Field, Modal, PasswordInput, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { makeEmailPrimary, type AccountEmail } from "../../../../api/accountEmails";
import { useEmailFormSubmit } from "./useEmailFormSubmit";

interface MakePrimaryDialogProps {
  target: AccountEmail;
  current: string;
  onClose: () => void;
  onDone: () => void;
}

/** Swaps a confirmed secondary email with the primary one, after the current password. */
export function MakePrimaryDialog({ target, current, onClose, onDone }: MakePrimaryDialogProps): React.JSX.Element {
  const { show } = useToast();
  const [password, setPassword] = useState("");
  const { loading, errors, run } = useEmailFormSubmit("We couldn't change your primary email.");

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    if (!password) return;
    await run(async () => {
      await makeEmailPrimary(target.id, password);
      show({ title: `Your email is now ${target.email}`, text: "Use it from now on to sign in.", tone: "success" });
      onDone();
    });
  }

  return (
    <Modal
      open
      icon="mail"
      title="Make primary"
      description={`You'll sign in with ${target.email}. ${current} stays on your account as another confirmed email.`}
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Make primary" loading={loading} disabled={!password} onClick={() => void submit()} />
        </>
      }
    >
      <form onSubmit={(e) => void submit(e)} className="nb-stack" noValidate>
        <Field label="Current password" help="To make sure it's you." error={errors.password}>
          <PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {errors.other ? <Alert tone="danger">{errors.other}</Alert> : null}
      </form>
    </Modal>
  );
}
